import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { authApi, TenantBranding } from '../api/auth';
import { useAuth } from '../contexts/AuthContext';
import PlatformLanding from '../platform/pages/Landing';
import TenantSite from './TenantSite';
import MinimalLanding from './MinimalLanding';

/**
 * Catch-all for non-root, non-API paths (e.g. /about). Unlike RootRedirect it
 * never bounces a signed-in admin to the console — an admin must be able to view
 * and inline-edit CMS pages. The full-site decision is still server-driven.
 *
 * On the platform domain (no tenant) an unknown path falls back to the marketing
 * home. On a tenant it renders the CMS page (full site on) or the minimal landing
 * (off) — except org admins always get the CMS page, so they can keep building
 * the site out before flipping it public; ApiGenericPage inside TenantSite
 * (via SiteAdminChrome) 404s unknown slugs itself.
 */
const TenantPage: React.FC = () => {
  const { isOrgAdmin } = useAuth();
  const { data: branding, isLoading } = useQuery<TenantBranding>({
    queryKey: ['tenant_branding'],
    queryFn: authApi.getBranding,
    staleTime: 10 * 60 * 1000,
  });

  if (isLoading) {
    return <div data-testid="tenant-page-loading">Loading…</div>;
  }

  if (!branding?.name) {
    return <PlatformLanding />;
  }

  if (!branding.full_site_enabled && !isOrgAdmin) {
    return <MinimalLanding />;
  }

  return <TenantSite />;
};

export default TenantPage;
